import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { modelApi } from '../../api/modelApi';
import type { JudgeRun } from '../../types/scenarioEvals';
import { DataTable, type ColumnDef } from '@shared-ui/components';

const pageSize = 20;

/** Mean verdict score for a completed judge run (the batch's average). */
function meanScore(run: JudgeRun): string {
  const v = run.verdicts;
  if (!v || v.length === 0) return '-';
  return (v.reduce((s, x) => s + x.score, 0) / v.length).toFixed(2);
}

export function JudgeRunList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['judgeRuns', page],
    queryFn: () => modelApi.getJudgeRuns({ page, pageSize }),
  });

  const rows = data?.judgeRuns || [];
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const columns: ColumnDef<JudgeRun>[] = useMemo(() => [
    {
      key: 'date',
      header: 'Date',
      className: 'dt-col-date',
      render: (r) => (
        <div className="dc-stack">
          <span className="dc-date">{format(new Date(r.createdAt), 'MMM d, HH:mm')}</span>
          {r.status === 'judging' && <span className="dc-status dc-status--analyzing" title="Judge batch in flight">JUDGING</span>}
          {r.status === 'failed' && <span className="dc-status dc-status--failed" title={r.error || 'Judge batch failed'}>FAILED</span>}
        </div>
      ),
    },
    {
      key: 'run',
      header: 'Eval run',
      render: (r) => (
        <div className="dc-stack">
          <span className="dc-name">{r.evalRunId.slice(0, 12)}</span>
          <span className="dc-email">dataset {r.datasetId.slice(0, 10)}</span>
        </div>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (r) => <span>{r.batch?.length ?? 0}</span>,
    },
    {
      key: 'meanScore',
      header: 'Mean verdict',
      render: (r) => <span>{meanScore(r)}</span>,
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (r) => <span className="provider-badge">{r.provider || '-'}</span>,
    },
    {
      key: 'prompt',
      header: 'Judge prompt',
      render: (r) => <span>{r.judgePromptId ? `${r.judgePromptId}@${r.judgePromptVersion ?? '?'}` : '-'}</span>,
    },
  ], []);

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <DataTable
      columns={columns}
      data={rows}
      rowKey={(r) => r.id}
      onRowClick={(r) => navigate(`/scenario-evals/${r.datasetId}`)}
      pagination={{ page, totalPages, total: data?.total, onPageChange: setPage }}
      loading={isLoading}
      emptyMessage="No judge runs yet."
    />
  );
}
